import type { UseCallRouterReturn } from './index.types';
import { MobXProviderContext } from 'mobx-react';
import { useNotificationStore } from 'Modules/notification/store';
import { callRouterLogger as logger } from 'Modules/softphone/utils/logger';
import { useCallback, useContext, useRef } from 'react';
import type { PhoneStore } from 'Stores/PhoneStore';
import { RootStoreProps } from 'Stores/RootStore.types';
import { useSoftphoneCoordinatorStore, useSoftphoneStore } from '../../store';
import { useSoftphoneCalls } from '../useSoftphoneCalls';

/**
 * useCallRouter Hook
 *
 * Single unified hook that routes calls between legacy PhoneStore and new softphone.
 * Encapsulates all routing logic and implementations in one place.
 *
 * NOTE: This hook only handles routing decision. All call management logic
 * should be in useSoftphoneCalls or PhoneStore.
 */
export const useCallRouter = (): UseCallRouterReturn => {
  // Get legacy PhoneStore from MobX context
  const mobxContext = useContext(MobXProviderContext) as RootStoreProps | null;
  const legacyPhoneStore: PhoneStore | undefined = mobxContext?.phoneCall;
  const personId = mobxContext?.person?.loggedInPersonId;

  // Get softphone preference from Zustand coordinator store
  const softphoneCoordinatorStore = useSoftphoneCoordinatorStore(personId);
  const useNewSoftphone = softphoneCoordinatorStore(
    (state) => state.isNewSoftphoneEnabled
  );

  const { makeCall } = useSoftphoneCalls();

  // Double-click protection - tracks calls in progress
  const callInProgressRef = useRef(false);

  const callWithPerson = useCallback(
    async (targetPersonId?: string, phone?: string): Promise<void> => {
      // Prevent double-click - ignore if a call is already in progress
      if (callInProgressRef.current) {
        logger.warn('Call already in progress, ignoring duplicate request', {
          personId: targetPersonId,
          phone,
        });
        return;
      }

      const currentUseNewSoftphone =
        softphoneCoordinatorStore.getState().isNewSoftphoneEnabled;

      logger.info('Routing call', {
        personId: targetPersonId,
        phone,
        useNewSoftphone: currentUseNewSoftphone ? 'Yes' : 'No',
      });

      // Validate input
      if (!targetPersonId && !phone) {
        const error = new Error(
          'Either Phone Number or Person Id is required.'
        );
        logger.error('Either Phone Number or Person Id is required.', {
          error: error.message,
        });
        throw error;
      }

      // Mark that a call is in progress
      callInProgressRef.current = true;

      try {
        if (currentUseNewSoftphone) {
          logger.info('Using new softphone');

          const { status } = useSoftphoneStore.getState();
          if (status !== 'registered') {
            logger.error('Softphone not ready', { status });

            useNotificationStore.getState().addNotify({
              type: 'error',
              title: 'Call Failed',
              message:
                'Unable to make call. SIP registration is not active. Please check your connection and try again.',
              dismissible: true,
              autoDismiss: false,
            });

            throw new Error(
              `Cannot make call: Softphone is ${status}. Please wait for connection or check your settings.`
            );
          }

          if (targetPersonId) {
            logger.info('[useCallRouter] Making internal call to person:', {
              personId: targetPersonId,
            });
            const internalTarget = `pr${targetPersonId}`;
            await makeCall(internalTarget, targetPersonId);
          } else if (phone) {
            logger.info('[useCallRouter] Making external call to:', { phone });
            await makeCall(phone);
          }
        } else {
          logger.info('Using legacy PhoneStore');

          if (!legacyPhoneStore) {
            logger.error(
              '[useCallRouter] PhoneStore not available in MobX context'
            );
            throw new Error(
              'PhoneStore not available in MobX context. Ensure component is wrapped in MobX Provider.'
            );
          }

          await legacyPhoneStore.callWithPerson(targetPersonId, phone);
        }

        logger.info('Call initiated successfully');
      } catch (error) {
        logger.error('Error making call:', { error: (error as Error).message });
        throw error;
      } finally {
        // Release the lock after a small delay to prevent very fast clicks
        setTimeout(() => {
          callInProgressRef.current = false;
        }, 1000);
      }
    },
    [softphoneCoordinatorStore, makeCall, legacyPhoneStore]
  );

  return {
    callWithPerson,
    isUsingNewSoftphone: useNewSoftphone,
  };
};
